import type { ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import type { AuthUser } from "@themislex/shared";
import { Emblem } from "./Emblem";
import { Button } from "./ui";

export interface NavItem {
  to: string;
  label: string;
}

export function AppShell({
  firmName,
  user,
  nav,
  onSignOut,
  children,
}: {
  firmName: string;
  user: AuthUser;
  nav: NavItem[];
  onSignOut: () => void;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-bg">
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      <header className="site-header border-b border-border bg-surface">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <Link to="/" className="flex items-center gap-3 text-ink no-underline">
            <Emblem size={42} />
            <span>
              <span className="block font-heading text-lg font-bold leading-tight">
                {firmName}
              </span>
              <span className="block text-[11px] font-medium uppercase tracking-[0.22em] text-muted">
                Client Portal
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm sm:block">
              <span className="font-medium">{user.name}</span>
              <span className="text-muted">
                {" "}
                · {user.role === "lawyer" ? "Chambers" : "Client"}
              </span>
            </span>
            <Button variant="ghost" onClick={onSignOut}>
              Sign out
            </Button>
          </div>
        </div>
        <nav aria-label="Primary" className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-4 pb-2">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main id="main" className="mx-auto max-w-6xl px-4 py-8">
        {children}
      </main>

      <footer className="gold-rule mt-auto border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-6 text-center text-xs text-muted">
          <p className="font-heading text-sm italic">Fiat justitia, ruat caelum.</p>
          <p className="mt-1">
            {firmName} · Client Portal
          </p>
        </div>
      </footer>
    </div>
  );
}

export const LAWYER_NAV: NavItem[] = [
  { to: "/desk", label: "Desk" },
  { to: "/desk/clients", label: "Clients" },
  { to: "/desk/matters", label: "Matters" },
  { to: "/desk/invoices", label: "Invoices" },
  { to: "/desk/settings", label: "Settings" },
];

export const CLIENT_NAV: NavItem[] = [
  { to: "/portal", label: "My matters" },
  { to: "/portal/invoices", label: "My invoices" },
  { to: "/portal/profile", label: "My profile" },
];
